import React from "react";
import styled, { FlattenSimpleInterpolation } from "styled-components";

import breakpoints from "../../../styles/breakpoints";
import { colors, fonts, header, transition } from "../../../styles/variables";

import {
  InfoBoxItem,
  InfoBoxItemDescriptionHolder,
  InfoBoxItemProductHolder,
  InfoBoxItemProductHolderButtons,
} from "../../atoms/SpecialInfoBoxItem";
import arrowsExpandable from "../../../assets/images/icons/arrows-expandable.svg";
import zoomIcon from "../../../assets/images/icons/zoom-icon.png";
import starFill from "../../../assets/images/icons/start-fill.svg";
import penIcon from "../../../assets/images/icons/pen.svg";

/* Top-left float (back arrow when rendered as a tray). Absolute-
 * positioned within ProductHero, which has position: relative and
 * sits below Layout's TopInsetSpacer (the parent .content / tray
 * container folds env(safe-area-inset-top) into its padding). */
export const CloseTrayButton = styled.div<{ isAddWineTrayOpen: boolean }>`
  position: absolute;
  z-index: 1;
  left: 20px;
  top: 20px;
  align-items: center;
  display: ${(props) => (props.isAddWineTrayOpen ? "none" : "flex")};
  justify-content: center;
  border-radius: 25px;
  width: 40px;
  height: 40px;
  > img {
    width: 20px;
    height: 20px;
    cursor: pointer;
  }
`;

/* The wine hero box. Plain coloured flex container — the notch zone
 * is painted by Layout's TopInsetSpacer, driven by useTopInsetBg() at
 * the render site. */
export const ProductHero = styled.div<{ isInSideTray: boolean; $bg: string }>`
  position: relative;
  background-color: ${(props) => props.$bg};
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: ${(props) => (props.isInSideTray ? 580 : 860)}px;
  justify-content: space-between;
  align-items: center;
  ${breakpoints.smDown} {
    height: auto;
  }
`;

export const IconContainer = styled.div<{ isAddWineTrayOpen?: boolean }>`
  align-items: center;
  display: ${(props) => (props.isAddWineTrayOpen ? "none" : "flex")};
  justify-content: center;
  position: relative;
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background-color: ${colors.whiteTransparent};
  cursor: pointer;
  margin: 0 0 16px auto;
  &:hover {
    background-color: ${colors.white};
  }
`;

export const Title = styled.div`
  font-family: ${fonts.fontInterstateBlackCompressed};
  font-size: 45px;
  line-height: 45px;
  margin-bottom: 24px;
  ${breakpoints.smDown} {
    font-size: 40px;
    line-height: 40px;
    margin-bottom: 16px;
  }
`;

export const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 40px;
  ${breakpoints.smDown} {
    margin-bottom: 24px;
  }
`;

export const Column = styled.div<{ isInSideTray?: boolean }>`
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${breakpoints.smDown} {
    & > *:last-child {
      ${(props) => props.isInSideTray && "padding-bottom: 120px"};
    }
  }
`;

export const VerticalLine = styled.div`
  width: 1px;
  height: 17px;
  margin-left: 10px;
  margin-right: 10px;
  background-color: ${colors.white};
`;

export const StyledAddWineToShoppingCartButton = styled.button<{
  css: [string] | undefined;
}>`
  font-family: ${fonts.fontBauMedium};
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 15px;
  cursor: pointer;
  text-transform: uppercase;
  background-color: ${colors.black};
  color: ${colors.white};
  font-size: 12px;
  flex: 1;
  width: 100%;
  ${(props) => props.css}
`;

export const StyledInfoBoxItemProductHolderButtons = styled((props) => (
  <InfoBoxItemProductHolderButtons {...props} />
))<{ shouldShowFixedAddBottleButton: boolean; isInBoxAndCartTray?: boolean }>`
  ${breakpoints.smDown} {
    width: ${(props) => (props.shouldShowFixedAddBottleButton ? "100%" : "167px")};
    position: ${(props) => (props.shouldShowFixedAddBottleButton ? "fixed" : "sticky")};
    bottom: ${(props) => (props.shouldShowFixedAddBottleButton ? 25 : 0)}px;
    z-index: 25;
    ${(props) => props.shouldShowFixedAddBottleButton && "padding: 0px 15px 15px"};
  }
  ${(props) => props.css}
`;

export const StyledInfoBoxItemProductHolder = styled((props) => <InfoBoxItemProductHolder {...props} />)`
  ${breakpoints.smUp} {
    position: sticky;
    left: 0;
    top: ${header.mobileHeight};
  }
  ${breakpoints.lgUp} {
    top: ${header.desktopHeight};
  }
  transition: ${transition.headerMenuBarTransition};
  ${(props) => props.css}
`;

export const StyledInfoBoxItem = styled((props) => <InfoBoxItem {...props} />)`
  width: 100%;
  align-items: flex-start;
  ${breakpoints.smDown} {
    overflow-y: scroll;
  }
  ${(props) => props.css}
`;

export const InfoBoxItemDescriptionWrapper = styled.div<{
  css: FlattenSimpleInterpolation | undefined;
}>`
  display: flex;
  flex-direction: column;
  ${(props) => props.css}
  ${breakpoints.smDown} {
    background-color: ${colors.peach};
  }
`;

export const StyledInfoBoxItemDescriptionHolder = styled((props) => (
  <InfoBoxItemDescriptionHolder {...props} />
))<{ withExtraBottomSpacing: boolean }>`
  width: 100% !important;
  ${(props) => props.withExtraBottomSpacing && "padding-bottom: 70px;"}
  ${(props) => props.css}
  background-color: ${colors.white};
  padding-bottom: 0;
`;

export const PronunciationText = styled.div``;

export const MatchAndRatingInfoWrapper = styled.div<{
  isRatingLabel?: boolean;
}>`
  padding: ${(props) => (props.isRatingLabel ? "8px 10px" : "8px 16px")};
  align-items: center;
  justify-content: center;
  position: relative;
  border-radius: 99px;
  background-color: ${colors.whiteTransparent};
  border: 1px solid ${colors.black};
  cursor: pointer;
  margin-bottom: 16px;
  font-size: ${(props) => (props.isRatingLabel ? "12" : "14")}px;
  &:hover {
    background-color: ${colors.white};
  }
`;

export const ReviewLabelWrapper = styled.div`
  font-size: 10px;
  font-family: ${fonts.fontBauMedium};
  line-height: 14px;
  text-decoration: underline;
  text-transform: uppercase;
  cursor: pointer;
  margin-bottom: 16px;
  justify-content: right;
  display: flex;
`;

export const MatchAndRatingInfoLabel = styled.div<{ isRating?: boolean }>`
  display: inline-block;
  font-size: ${(props) => (props.isRating ? 12 : 14)}px;
  line-height: 17px;
  &:hover {
    background-color: ${colors.white};
  }
`;

/* Top-right float — match% / bookmark / rating cluster. Absolute-
 * positioned within ProductHero, which sits below Layout's
 * TopInsetSpacer regardless of tray-vs-in-flow context. */
export const HeroImageTopRightSection = styled.div`
  position: absolute;
  z-index: 1;
  right: 20px;
  top: 30px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-family: ${fonts.fontBauMedium};
`;

export const ArrowsExpandableIcon = styled.img.attrs(() => ({ src: arrowsExpandable }))`
  width: 18px;
  height: 18px;
`;

export const InfoBoxItemProductImage = styled.img<{
  isInSideTray: boolean;
  isPressed: boolean;
}>`
  position: relative;
  object-fit: cover;
  height: ${(props) => (props.isInSideTray ? 409 : 620)}px;
  transition: ${transition.subMenuTransition};
  margin-top: 40px;
  ${breakpoints.mdUp} {
    &:hover {
      cursor: url("${zoomIcon}"), pointer;
      transform: scale(${(props) => (props.isInSideTray ? 2.2 : 1.8)})
        translate(0, ${(props) => (props.isInSideTray ? -100 : -135)}px);
    }
  }
  ${breakpoints.mdDown} {
    ${(props) =>
      !props.isInSideTray &&
      `
      width: 192px;
      height: 409px;
      `}
    transform: ${(props) => (props.isPressed ? "scale(2.5) translate(0, -120px)" : "scale(1) translate(0)")};
  }
`;

/* InfoBoxItemProductMediaHolder removed — its job (coloured bg, fixed
 * height on smUp, top-floating slots) is now handled by ProductHero
 * (a styled extension of the PageHero atom). */

export const ProductDetailsTagsWrapper = styled.div<{
  isWineDetailsPage: boolean;
}>`
  position: relative;
  padding: 24px 14px;
  bottom: 0px;
  width: 100%;
  font-family: ${fonts.fontBauRegular};
  ${(props) => props.isWineDetailsPage && "bottom: 60px"}

  ${breakpoints.smDown} {
    bottom: 0px;
  }
`;

export const StarIcon = styled.img.attrs(() => ({ src: starFill }))`
  width: 11px;
  height: 11px;
  margin: 0px 4px;
`;

export const PenIcon = styled.img.attrs(() => ({ src: penIcon }))`
  width: 13px;
  height: 13px;
  margin: 0px 4px;
`;

export const TasteProfileWrapper = styled.div<{ isWineDetailsPage: boolean }>`
  ${breakpoints.smDown} {
    scroll-margin-top: ${(props) => (props.isWineDetailsPage ? 80 : 20)}px;
  }
`;
